import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, Building2, Pencil } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatDate, formatQuantity, unitOfMeasureAbbr } from "@/lib/format";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TH } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocStatusBadge, LineMatchBadge } from "@/components/PoDnStatus";
import { SupplierCard } from "@/components/SupplierCard";
import { useSupplier } from "@/hooks/useSupplier";
import { DeliveryNoteDialog } from "@/components/DeliveryNotesPage";
import { MatchProgress, MatchedBillsCard } from "@/components/PurchaseOrderDetailPage";
import { toastApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";

type DeliveryNoteResponse = components["schemas"]["DeliveryNoteResponse"];
type PurchaseOrderResponse = components["schemas"]["PurchaseOrderResponse"];
type MatchedBillView = components["schemas"]["MatchedBillView"];

export function DeliveryNoteDetailPage({
  deliveryNoteId,
  onBack,
  onMatchClick,
  onSupplierClick,
  onPurchaseOrderClick,
}: {
  deliveryNoteId: string;
  onBack?: () => void;
  /** Click-through for a matched bill (receives the bill id) — opens the three-way match view. */
  onMatchClick?: (billId: string) => void;
  onSupplierClick?: (supplierId: string) => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
}) {
  const api = useDolfinClient();
  const [dn, setDn] = useState<DeliveryNoteResponse | null>(null);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderResponse | null>(null);
  const [matchedBills, setMatchedBills] = useState<MatchedBillView[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acting, setActing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { supplier } = useSupplier(dn?.supplierId);

  const fetchDn = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    const [{ data, error }, matched] = await Promise.all([
      api.GET("/v1/delivery-notes/{id}", { params: { path: { id: deliveryNoteId } } }),
      api.GET("/v1/delivery-notes/{id}/matched-bills", { params: { path: { id: deliveryNoteId } } }),
    ]);
    if (error || !data) setNotFound(true);
    else setDn(data);
    setMatchedBills(matched.data ?? []);
    setLoading(false);
  }, [api, deliveryNoteId]);

  useEffect(() => {
    fetchDn();
  }, [fetchDn]);

  // The note carries only the order's id; its number is what a person recognises.
  const purchaseOrderId = dn?.purchaseOrderId;
  useEffect(() => {
    if (!purchaseOrderId) {
      setPurchaseOrder(null);
      return;
    }
    let cancelled = false;
    api
      .GET("/v1/purchase-orders/{id}", { params: { path: { id: purchaseOrderId } } })
      .then(({ data }) => {
        if (!cancelled) setPurchaseOrder(data ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, purchaseOrderId]);

  const back = onBack ? <BackLink onClick={onBack}>Back to delivery notes</BackLink> : null;

  // Manual terminal transitions (the only way to reach Closed/Cancelled — matching never sets them).
  const runTransition = useCallback(
    async (action: "close" | "cancel", verb: string, call: () => Promise<{ error?: unknown }>) => {
      setActing(true);
      const { error } = await call();
      setActing(false);
      if (error) {
        toastApiError(error, `Couldn't ${action} the delivery note.`);
        return;
      }
      toast.success(`Delivery note ${verb}.`);
      fetchDn();
    },
    [fetchDn],
  );

  if (loading && !dn) {
    return (
      <PageContainer>
        {back}
        <LoadingState label="Loading delivery note..." />
      </PageContainer>
    );
  }

  if (notFound || !dn) {
    return (
      <PageContainer>
        {back}
        <ErrorState message="That delivery note could not be found." onRetry={fetchDn} />
      </PageContainer>
    );
  }

  const lines = [...(dn.lines ?? [])].sort(
    (a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0),
  );
  const received = lines.reduce((sum, l) => sum + Number(l.quantityReceived ?? 0), 0);
  const isTerminal = dn.status === "Closed" || dn.status === "Cancelled";
  const matchedLines = lines.filter((l) => l.fullyMatched).length;
  const supplierName = supplier?.name ?? null;

  return (
    <PageContainer>
      {back}
      <PageHeader
        eyebrow="Delivery note"
        title={<span className="font-mono">{dn.dnNumber || "Delivery note"}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-foreground tabular-nums">
              {formatQuantity(received)}
            </span>
            <span>received</span>
            {dn.receivedAt ? <span>{formatDate(dn.receivedAt)}</span> : null}
            {supplierName ? (
              <>
                <span aria-hidden>·</span>
                <span>{supplierName}</span>
              </>
            ) : null}
            <span aria-hidden>·</span>
            <span>
              {lines.length} line{lines.length === 1 ? "" : "s"}
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <DocStatusBadge status={dn.status} />
            {!isTerminal && (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-3.5" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={acting}
                  onClick={() =>
                    runTransition("close", "closed", () =>
                      api.POST("/v1/delivery-notes/{id}/close", {
                        params: { path: { id: deliveryNoteId } },
                      }),
                    )
                  }
                >
                  Close
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={acting}
                  onClick={() =>
                    runTransition("cancel", "cancelled", () =>
                      api.POST("/v1/delivery-notes/{id}/cancel", {
                        params: { path: { id: deliveryNoteId } },
                      }),
                    )
                  }
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                <dt className="text-muted-foreground">Received</dt>
                <dd>{dn.receivedAt ? formatDate(dn.receivedAt) : "-"}</dd>
                <dt className="text-muted-foreground">Purchase order</dt>
                <dd>
                  {!dn.purchaseOrderId ? (
                    "-"
                  ) : onPurchaseOrderClick ? (
                    <button
                      type="button"
                      onClick={() => onPurchaseOrderClick(dn.purchaseOrderId!)}
                      className="inline-flex items-center gap-1 font-mono text-xs font-semibold hover:underline"
                    >
                      {purchaseOrder?.poNumber ?? "Open the order"}
                      <ArrowUpRight className="size-3.5 text-muted-foreground" />
                    </button>
                  ) : (
                    <span className="font-mono text-xs">{purchaseOrder?.poNumber ?? "-"}</span>
                  )}
                </dd>
                <dt className="text-muted-foreground">External reference</dt>
                <dd className="font-mono text-xs">{dn.externalRef || "-"}</dd>
                <dt className="text-muted-foreground">Recorded</dt>
                <dd>{formatDate(dn.createdAt)}</dd>
              </dl>
            </CardContent>
          </Card>

          {dn.supplierId ? (
            <SupplierCard supplierId={dn.supplierId} onSupplierClick={onSupplierClick} />
          ) : (
            <EmptyState icon={Building2} title="No supplier yet">
              Assign a supplier so these quantities can be reconciled against their bills.
            </EmptyState>
          )}
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-base">Lines</CardTitle>
            <MatchProgress matched={matchedLines} total={lines.length} />
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Description</TableHead>
                    <TableHead className={TH}>Ext. ref</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Received</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Matched</TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="max-w-[360px]">
                        <div className="truncate font-medium">{l.description}</div>
                        {l.sku ? (
                          <div className="truncate font-mono text-xs text-muted-foreground">SKU {l.sku}</div>
                        ) : null}
                      </TableCell>
                      {/* The integrator's own line reference, echoed so they can confirm the mapping. Not used for matching. */}
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {l.externalRef || "-"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap tabular-nums">
                        {formatQuantity(l.quantityReceived)}
                        <span className="ml-1 text-muted-foreground">
                          {unitOfMeasureAbbr(l.unitOfMeasure) ?? l.unit ?? ""}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex justify-end">
                          <LineMatchBadge
                            matchedQuantity={l.matchedQuantity}
                            totalQuantity={l.quantityReceived}
                            fullyMatched={l.fullyMatched}
                          />
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <MatchedBillsCard
          bills={matchedBills}
          // A delivery note has no currency; the order it was despatched against does.
          currency={purchaseOrder?.currency ?? "GBP"}
          onMatchClick={onMatchClick}
          emptyText="Dolfin reconciles the quantities received against this supplier's bills as they arrive."
        />
      </div>

      <DeliveryNoteDialog
        open={editOpen}
        editing={dn}
        onOpenChange={setEditOpen}
        onSaved={() => fetchDn()}
      />
    </PageContainer>
  );
}
